import type { ReactNode } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  NarrativeKeyMetric,
  NarrativeKeyMetricChart,
} from './components/NarrativeKeyMetric';
import { SharePrice } from './components/SharePrice';
import { usePriceHistoryData } from '@components/MiniPriceChart';
import { useNarrativeChartSelection } from '@/components/NarrativeTable/components/TableRow/hooks/useNarrativeChartSelection';
import { useNarrativeValuation } from './hooks/useNarrativeValuation';
import { selector } from './selector';
import { cn } from '@simplywallst/ui-core/next';
import { CURRENT_PERIOD } from './constants';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

const PositiveNegativeValue = ({
  value,
  children,
}: {
  value: number | null;
  children: ReactNode;
}) => (
  <span
    className={cn(
      value === 0 || value === null
        ? 'text-legacy-mediumgray'
        : value > 0
          ? 'text-good'
          : 'text-bad'
    )}
  >
    {children}
  </span>
);

const SmallLayout = ({
  children,
  companyId,
}: {
  children: ReactNode;
  companyId: string;
}) => {
  const { isNegativeMovement } = usePriceHistoryData(companyId, CURRENT_PERIOD);
  return (
    <div className="relative max-md:px-x2 max-md:py-x1_5 dark:max-md:[&_.visx-area-closed]:hidden">
      <div className="pointer-events-none absolute inset-0 md:hidden">
        <div className="border absolute inset-0 rounded-md border-subtle shadow light:shadow-sm" />
        {isNegativeMovement !== undefined && (
          <div
            className={cn(
              'absolute inset-px rounded-md bg-legacy-noir/20 backdrop-blur-lg light:hidden',
              'animate-fade-in bg-[linear-gradient(to_top_right,var(--tw-gradient-from),var(--tw-gradient-to)_65%)] to-transparent [animation-fill-mode:backwards]',
              isNegativeMovement ? 'from-bad-subtle' : 'from-good-subtle'
            )}
          />
        )}
      </div>
      <div className="relative z-1" id="price-analytics-card">
        {children}
      </div>
    </div>
  );
};

export const PriceAnalyticsCard = () => {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const companyId = selected.companyId ?? '';

  const { data: narrativeValuation, status: narrativeValuationStatus } =
    useNarrativeValuation(
      {
        companyId,
      },
      {
        enabled: !!selected?.companyId,
      }
    );

  const { date, fairValue, lastSharePrice, valuations, onHover } =
    useNarrativeChartSelection({
      company: narrativeValuation?.Company ?? null,
      defaultLastSharePrice: null,
    });

  const currencyISO = getTradingItemCurrencyISO(
    narrativeValuation?.Company?.data?.currencyInfo
  );

  const narrativeId =
    narrativeValuation?.Company?.myValuation?.narrative?.id ||
    narrativeValuation?.Company?.defaultNarrative?.id;

  const owner =
    narrativeValuation?.Company?.myValuation?.narrative?.owner ||
    narrativeValuation?.Company?.defaultNarrative?.owner;

  const isNegativeMovement =
    selected.return1Y !== null ? selected.return1Y < 0 : undefined;

  if (selected.status === 'loading') return null;

  return (
    <SmallLayout companyId={companyId}>
      <div className="grid grid-cols-[92px_auto] gap-1.5">
        <SharePrice
          companyId={companyId}
          time={date}
          sharePrice={lastSharePrice}
          currencyISO={selected.tradingItemCurrencyISO}
        />
        <NarrativeKeyMetric
          companyId={companyId}
          sharePrice={lastSharePrice}
          fairValue={fairValue}
          currencyISO={currencyISO}
          npvPerShare={narrativeValuation?.Company?.analysisValue?.npvPerShare}
          narrativeId={narrativeId}
          owner={owner}
          status={narrativeValuationStatus}
        />
      </div>
      <div className="mt-2">
        <NarrativeKeyMetricChart
          companyId={companyId}
          status={narrativeValuationStatus}
          valuations={valuations}
          onHover={onHover}
          isNegativeMovement={isNegativeMovement}
        />
      </div>
      <div className="grid grid-cols-[92px_auto] gap-2 text-xs text-soft sm:grid-cols-[70px_auto] print:text-p-black">
        <div>
          <span className="mr-1" aria-label="1 year change">
            1Y
          </span>
          <span>
            <PositiveNegativeValue value={selected.return1Y}>
              {selected.return1YFormatted}
            </PositiveNegativeValue>
          </span>
        </div>
        <div>
          <span className="mr-1" aria-label="7 day change">
            7D
          </span>
          <span className="text-solid">{selected.return7DFormatted}</span>
        </div>
      </div>
    </SmallLayout>
  );
};
